import { useEffect, useState } from 'react'
import type { BannerImage } from './getBannerImage'
import { decodeImage, getImagePreloadRequest } from './getBannerImage'

/**
 * Keeps showing the current banner image until the next one has decoded, then swaps the image, its
 * anchor and the home-image tag together. An `<img>` whose `src` changes keeps painting the old
 * pixels until the new image decodes, while its new `object-position` applies at once, so the old
 * image showed at the new anchor for a frame (#25097). The first render is never held back, and a
 * decode waits at most `DECODE_TIMEOUT_MS`.
 *
 * `next` must keep its identity between renders (memoize it): every new object starts a decode.
 */
export function useDecodedBannerImage(next: BannerImage): BannerImage {
  const [shown, setShown] = useState(next)

  useEffect(() => {
    const request = typeof Image === 'undefined' ? null : getImagePreloadRequest(next.image)
    if (!request) {
      setShown(next)
      return
    }
    let cancelled = false
    // decodeImage never rejects
    void decodeImage(request).then(() => {
      if (!cancelled) {
        setShown(next)
      }
    })
    return () => {
      cancelled = true
    }
  }, [next])

  return shown
}
